/**
 * Weekly Report Page (Super Admin)
 *
 * Generates a PDF slide deck with P&L, order flows, and support metrics
 * for the selected week vs trailing 30-day average.
 */

// Local calendar dates: toISOString() is UTC, which moves local midnight to
// the previous day for anyone east of Greenwich.
import { format } from 'date-fns'
import { useState, useMemo } from 'react'
import { dashboardAPI, orderFlowsAPI, zendeskAPI } from '../services/api'
import {
  Card, CardContent, CardHeader, CardTitle,
  Button
} from '../components/ui'
import {
  FileDown, Loader2, AlertCircle, ChevronLeft, ChevronRight, CheckCircle
} from 'lucide-react'
import { generateWeeklyReportPdf } from '../utils/weeklyReportPdf'

function getLastCompletedWeek() {
  const now = new Date()
  const day = now.getDay() // 0=Sun
  const daysToLastMonday = day === 0 ? 6 : day + 6
  const monday = new Date(now)
  monday.setDate(now.getDate() - daysToLastMonday)
  monday.setHours(0, 0, 0, 0)
  return monday
}

function formatDateStr(date) {
  return format(date, 'yyyy-MM-dd')
}

function formatDisplayDate(dateStr) {
  const d = new Date(dateStr + 'T12:00:00')
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function addDays(dateStr, days) {
  const d = new Date(dateStr + 'T12:00:00')
  d.setDate(d.getDate() + days)
  return formatDateStr(d)
}

export default function WeeklyReportPage() {
  const [weekMonday, setWeekMonday] = useState(() => formatDateStr(getLastCompletedWeek()))
  const [loading, setLoading] = useState(false)
  const [progress, setProgress] = useState('')
  const [error, setError] = useState(null)
  const [done, setDone] = useState(false)

  const weekSunday = useMemo(() => addDays(weekMonday, 6), [weekMonday])
  const t30End = useMemo(() => addDays(weekMonday, -1), [weekMonday])
  const t30Start = useMemo(() => addDays(weekMonday, -30), [weekMonday])

  const shiftWeek = (direction) => {
    setWeekMonday(prev => addDays(prev, direction * 7))
    setDone(false)
  }

  const generate = async () => {
    try {
      setLoading(true)
      setError(null)
      setDone(false)

      // Phase 1: Fetch aggregate data (P&L + Support) for week and trailing 30
      setProgress('Fetching P&L and support data...')
      const [pnlWeekRes, pnlT30Res, supportWeekRes, supportT30Res] = await Promise.all([
        dashboardAPI.getAggregatePnL({ startDate: weekMonday, endDate: weekSunday }),
        dashboardAPI.getAggregatePnL({ startDate: t30Start, endDate: t30End }),
        zendeskAPI.getReportAggregate({ startDate: weekMonday, endDate: weekSunday }).catch(() => null),
        zendeskAPI.getReportAggregate({ startDate: t30Start, endDate: t30End }).catch(() => null),
      ])

      const pnlWeek = pnlWeekRes.data?.data
      const pnlT30 = pnlT30Res.data?.data
      const supportWeek = supportWeekRes?.data
      const supportT30 = supportT30Res?.data

      // Extract brand list from P&L response
      const brands = pnlWeek?.brand_breakdown || []
      const clinicIds = brands.map(b => b.clinicId)

      // Phase 2: Fetch per-brand order flows
      setProgress(`Fetching order flows for ${clinicIds.length} brands...`)
      const orderFlowResults = await Promise.allSettled(
        clinicIds.map(clinicId =>
          orderFlowsAPI.getStepAnalysis({
            source_clinic: clinicId,
            startDate: weekMonday,
            endDate: weekSunday,
            compStartDate: t30Start,
            compEndDate: t30End,
            step: 'all',
            excludeWeekends: 'false',
          })
        )
      )

      const orderFlows = {}
      clinicIds.forEach((clinicId, i) => {
        const brand = brands.find(b => b.clinicId === clinicId)
        if (orderFlowResults[i].status === 'fulfilled') {
          orderFlows[clinicId] = {
            displayName: brand?.displayName || clinicId,
            data: orderFlowResults[i].value.data?.data,
          }
        } else {
          orderFlows[clinicId] = {
            displayName: brand?.displayName || clinicId,
            data: null,
          }
        }
      })

      // Phase 3: Generate PDF
      setProgress('Generating PDF...')
      await new Promise(r => setTimeout(r, 50)) // Let UI update

      const doc = generateWeeklyReportPdf({
        weekStart: formatDisplayDate(weekMonday),
        weekEnd: formatDisplayDate(weekSunday),
        pnlWeek,
        pnlT30,
        orderFlows,
        supportWeek,
        supportT30,
      })

      doc.save(`weekly-report-${weekMonday}.pdf`)
      setDone(true)
      setProgress('')
    } catch (err) {
      console.error('[WEEKLY-REPORT] Error:', err)
      setError(err.response?.data?.error?.message || err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Weekly Report</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Generate a PDF slide deck with P&L, order flows, and support metrics
        </p>
      </div>

      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle className="text-base">Select Week</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Week selector */}
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => shiftWeek(-1)} disabled={loading}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="text-center flex-1">
              <div className="font-medium">{formatDisplayDate(weekMonday)} — {formatDisplayDate(weekSunday)}</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                vs trailing 30: {formatDisplayDate(t30Start)} — {formatDisplayDate(t30End)}
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => shiftWeek(1)} disabled={loading}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {/* Generate button */}
          <Button
            onClick={generate}
            disabled={loading}
            className="w-full"
            size="lg"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {progress || 'Working...'}
              </>
            ) : done ? (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Downloaded — Generate Again?
              </>
            ) : (
              <>
                <FileDown className="h-4 w-4 mr-2" />
                Generate PDF Report
              </>
            )}
          </Button>

          {error && (
            <div className="flex items-start gap-2 text-destructive text-sm">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* What's included */}
      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle className="text-base">Report Contents</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-foreground font-medium w-24 flex-shrink-0">P&L</span>
              <span>All brands aggregate + per-brand breakdown with revenue, expenses, margin, orders vs trailing 30 avg</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-foreground font-medium w-24 flex-shrink-0">Order Flows</span>
              <span>Per-brand step durations (median, P90) for doctor, pharmacy, shipping, routing, total</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-foreground font-medium w-24 flex-shrink-0">Support</span>
              <span>Tickets created/solved, CSAT, first reply time, solve time — aggregate + per brand</span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  )
}
